import { useEffect, useState } from 'react';
import { InvoiceService } from 'api';
import { ClausesQueryParams } from 'api/invoices/invoices.types';
import { AutocompletePaginationAsync } from 'components/Inputs';
import { Clause } from 'pages/invoices/invoices.types';
import { FieldPath, FieldValues } from 'react-hook-form';
import { createHandleOnBlur, handleChange } from 'utils/form.utils';
import { RenderProps } from './types';

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  formProps: RenderProps<TFieldValues, TName>;
  options: Clause[];
  params: ClausesQueryParams;
  setParams: React.Dispatch<React.SetStateAction<ClausesQueryParams>>;
  setOptions: React.Dispatch<React.SetStateAction<Clause[]>>;
};

export default function ClausesAutocomplete<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  formProps,
  options,
  params,
  setParams,
  setOptions,
}: Props<TFieldValues, TName>) {
  const {
    getClauses,
    loading: clausesLoading,
    clausesData,
  } = InvoiceService.useGetClauses();

  useEffect(() => {
    getClauses({ ...params });
  }, [getClauses, params]);

  useEffect(() => {
    if (!clausesData) return;

    const enabled = clausesData.filter((c) => c.enabled);

    if (params.pageNumber === 1) {
      setOptions(enabled);
    } else {
      setOptions((prev) => [
        ...prev,
        ...enabled.filter((e) => !prev.some((p) => p.id === e.id)),
      ]);
    }
  }, [clausesData, params.pageNumber]);

  return (
    <AutocompletePaginationAsync
      {...formProps}
      width={280}
      padding='dense'
      multiple
      loading={clausesLoading}
      options={options}
      defaultValue={formProps.field.value || []}
      value={formProps.field.value}
      isOptionEqualToValue={(option, value) => value?.id === option?.id}
      getOptionLabel={(o) => o.message}
      getOptionDisabled={(o) => !o.enabled}
      handleSearchChange={handleChange(setOptions, setParams)}
      onScrollBottom={() =>
        setParams((prev) => ({
          ...prev,
          pageNumber: (prev.pageNumber += 1),
        }))
      }
      onBlur={createHandleOnBlur(setOptions, setParams, formProps.field.onBlur)}
      onChange={(_, data) => formProps.field.onChange(data)}
    />
  );
}
